import { Injectable } from '@angular/core';
import { SubscriptionFacadeService } from 'src/app/facades/subscription/subscription-facade.service';
import { ThemeService } from '../../theme/theme.service';
import { StorageService } from './storage.service';
import { AuthService } from '../../services/auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class DataPreloadService {
  constructor(
    private storageService: StorageService,
    private subscriptionFacade: SubscriptionFacadeService,
    private themeService: ThemeService,
    private authService: AuthService
  ) {}

  async prepare(): Promise<void> {
    try {
      this.themeService.initializeTheme();
      await this.restoreAuth();
      await this.preloadData();
    } catch (e) {
      console.warn('Preload prepare completed with error:', e);
    }
  }

  private async restoreAuth(): Promise<void> {
    const user = this.storageService.getUserData();
    if (!user?.isLoggedIn) {
      return;
    }

    // If access token is still current, continue normally
    if (this.storageService.isTokenCurrent(user.token)) {
      return;
    }

    // Access token is expired, but user has stored session. Refresh automatically!
    try {
      await this.authService.refreshToken(true);
    } catch (error) {
      console.warn('Startup session refresh failed:', error);
      // Only clear auth session if refresh genuinely failed (e.g. invalid session/user revoked)
      this.storageService.clearAuthSession();
    }
  }

  preloadData(): Promise<void> {
    return new Promise<void>((resolve) => {
      try {
        const retrieveData = this.storageService.getUserData();
        if (retrieveData?.isLoggedIn && retrieveData.sId && this.storageService.isAuthenticated()) {
          const data = { stylistId: retrieveData.sId };
          this.subscriptionFacade.getFeatureAccess(data).subscribe({
            next: (res: any) => {
              // Update storage with the retrieved data, keeping latest refreshed token
              const current = this.storageService.getUserData() || retrieveData;
              const updatedData = {
                ...current,
                "isCalendarAccess": res.isCalendarAccess,
                "isDoubleBookingAccess": res.isDoubleBookingAccess,
                "isRecurringAccess": res.isRecurringAccess,
                "isReportsAccess": res.isReportsAccess,
                "isEmailNotification": res.isEmailNotification,
                "isSMSNotification": res.isSMSNotification ?? res.isReportsAccess,
                "locationLimitReached": res.locationLimitReached,
                "notesAccess": res.notesAccess,
                "onlineBookAccess": res.onlineBookAccess,
                "subscriptionExpired": res.subscriptionExpired,
                "gracePeriod": res.gracePeriod,
                "subscriptionEndDate": res.subscriptionEndDate,
                "trialPeriod": res.isTrialPeriod
              };
              this.storageService.setUserData(updatedData);
              resolve();
            },
            error: () => {
              resolve(); // Prevent blocking on error
            },
          });
        } else {
          resolve(); // No user data or unauthenticated, resolve immediately
        }
      } catch (error) {
        console.error('Error in data preloading:', error);
        resolve(); // Prevent blocking on any unexpected error
      }
    });
  }
}